import { MicrosoftSSOBlocker } from '@components/sso/MicrosoftSSOBlocker';
import { Api } from '@config/api';
import { resolveVoiceNewBadge } from '@conversed/shared/src/central/helpers/voice-messages.helper';
import {
  resolveVoiceMode,
  VoiceMode,
} from '@conversed/shared/src/central/types/webclient-config.type';
import { useChatStatus } from '@helpers/useChatStatus';
import { getBypassToken, storeBypassToken } from '@services/ssoBypassService';
import type React from 'react';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Blocker } from '../components/Blocker';
import ChatTitle from '../components/ChatTitle';
import type { ActiveChatTab } from '../components/ChatVoiceTabBar';
import ChatVoiceTabBar from '../components/ChatVoiceTabBar';
import FullscreenToggleButton from '../components/FullscreenToggleButton';
import MessageBox from '../components/MessageBox';
import Messages from '../components/Messages';
import VoicePanel from '../components/VoicePanel';
import { AudioConfig } from '../config/audio';
import { ConfigContext } from '../context/ConfigContext';
import analyticsService from '../services/analyticsService';
import { default as io, default as ioService } from '../services/ioService';
import {
  getStorage,
  getUserId,
  default as localStorageService,
  patchStorage,
  default as storage,
} from '../services/localStorageService';
import {
  sendLocationRequest,
  sendToggleFullscreen,
} from '../services/postMessageService';
import querystringService from '../services/querystringService';
import { isJwtExpired, refreshSSOTokens } from '../services/ssoService';
import type {
  ChatActivityStatus,
  IDData,
  Message,
  MessageDirection,
  MessagePayload,
  StreamingState,
} from '../types/ChatWindow';
import { StreamPhase } from '../types/ChatWindow';
import type {
  ChatWindowState as ChatWindowTypeState,
  InitStateProps,
} from '../types/configs';
import type { MessageData } from '../types/io';
import Attachments from './Attachments';

// * Checks if all required MS SSO tokens are present under the configId-scoped storage entry
const getSSOTokens = (configId: string): boolean => {
  const { sso } = getStorage(configId);

  return Boolean(sso?.accessToken && sso?.refreshToken && sso?.idToken);
};

// * Voice-only widgets open directly on the Voice tab; every other mode opens on Chat.
const resolveInitialTab = (
  voice: Parameters<typeof resolveVoiceMode>[0],
): ActiveChatTab => {
  if (resolveVoiceMode(voice) === VoiceMode.VoiceOnly) {
    return 'voice';
  }

  return 'chat';
};

type ChatWindowProps = {
  chatEnabled: boolean;
  onEnableChat: () => void;
  allowTextInput: boolean;
  messages: Array<Message>;
  addMessage: (...args: any[]) => void;
  changeWindowState: (state: ChatWindowTypeState) => void;
  sendWindowStatus: ({ windowToggle, windowType }) => void;
  onClose: (...args: any[]) => void;
  registeredEvents: {
    set: (...args: any[]) => void;
    get: (...args: any[]) => any;
    delete: (...args: any[]) => void;
  };
  fullscreen: boolean;
  setMessages: (id: string) => void;
  data?: unknown;
  initState: (props: InitStateProps) => void;
  changeChatActivityStatus: (status: ChatActivityStatus) => void;
  chatActivityStatus: ChatActivityStatus;
  bannerMessage?: string;
  bannerStatus: 'info' | 'error';
  toggleConnectionStateBanner: (status: boolean) => void;
  isUserFullscreen: boolean;
  onFullscreenChange?: (isFullscreen: boolean) => void;
  // Called whenever the active bot flow block type changes so the parent can
  // gate the 45-second idle timeout to Handover only.
  onFlowStateChange?: (state: string) => void;
  onLanguageChange?: (language: string) => void;
};

export const ChatWindow: React.FC<
  ChatWindowProps & { hidden?: boolean; initWebSocket?: boolean }
> = ({
  hidden,
  initWebSocket,
  fullscreen,
  allowTextInput,
  chatEnabled,
  messages,
  onClose,
  onEnableChat,
  changeWindowState,
  addMessage,
  setMessages,
  registeredEvents,
  data,
  sendWindowStatus,
  initState,
  changeChatActivityStatus,
  chatActivityStatus,
  bannerStatus,
  bannerMessage,
  toggleConnectionStateBanner,
  isUserFullscreen,
  onFullscreenChange,
  onFlowStateChange,
  onLanguageChange,
}) => {
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // * Watchdog for streaming: if no textChunk arrives within this window after
  // * streamStart, we clear the streaming state so the user is never stuck on
  // * the "Thinking..." indicator indefinitely.
  const streamWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const STREAM_WATCHDOG_MS = 15_000;
  // * Holds the final streaming message while the typewriter finishes flushing.
  // * Cleared by handleStreamComplete once addMessage is called.
  const pendingFinalMessageRef = useRef<{
    direction: MessageDirection;
    params: MessagePayload;
    streamId: string;
  } | null>(null);

  const {
    scheme: {
      header: { headerColor },
    },
    isSoundEnabled,
    refParam,
    revisionId,
    configId,
    defaultState,
    botId,
    enableWaitingForBotResponseBlocker,
    requireMicrosoftLogin,
    voice,
    voiceMessages,
  } = useContext(ConfigContext);

  // * Resolve the three-state voice mode from the (possibly legacy) voice config.
  const voiceMode = resolveVoiceMode(voice);

  // * Tracks which tab is visible. Voice-only widgets initialise on the Voice
  // * tab, so no first frame shows the Chat tab before it is corrected.
  const [activeTab, setActiveTab] = useState<ActiveChatTab>(() =>
    resolveInitialTab(voice),
  );

  // Set to true by VoicePanel while a call is connecting or connected.
  const [isVoiceCallActive, setIsVoiceCallActive] = useState(false);

  // When the user tries to switch away from the Voice tab during an active call
  // we show an inline confirmation bar instead of switching immediately.
  const [pendingTab, setPendingTab] = useState<ActiveChatTab | null>(null);

  const handleTabChange = useCallback(
    (tab: ActiveChatTab) => {
      // Switching to voice or switching while no call is active - allow immediately.
      if (tab === 'voice' || !isVoiceCallActive) {
        setActiveTab(tab);
        return;
      }

      // Switching away from voice while a call is in progress - ask for confirmation.
      setPendingTab(tab);
    },
    [isVoiceCallActive],
  );

  const handleConfirmTabSwitch = useCallback(() => {
    if (pendingTab !== null) {
      setActiveTab(pendingTab);
    }
    setPendingTab(null);
  }, [pendingTab]);

  const handleCancelTabSwitch = useCallback(() => {
    setPendingTab(null);
  }, []);

  // If the call drops while the tab-switch confirmation bar is visible (e.g. network
  // error or remote hang-up), dismiss the bar automatically - there is no active call
  // to protect anymore.
  useEffect(() => {
    if (!isVoiceCallActive && pendingTab !== null) {
      setPendingTab(null);
    }
  }, [isVoiceCallActive, pendingTab]);

  // Determine if fullscreen is active
  const isFullscreenActive = isUserFullscreen || fullscreen;

  const elementClass = useMemo(
    () =>
      `chat-window-container ${isFullscreenActive ? 'full-screen' : ''} ${chatEnabled ? 'blocked' : ''} ${hidden ? 'hidden' : ''}`,
    [isFullscreenActive, chatEnabled, hidden],
  );

  const { setSendChatStatus } = useChatStatus();
  const [messageSound] = useState(new Audio(AudioConfig.messageSound));
  const [typingState, setTypingIndicator] = useState<
    'typing_on' | 'typing_off'
  >('typing_off');
  const [attachments, setAttachments] = useState([]);
  const [isSendMessageDisabled, setIsSendMessageDisabled] = useState(false);
  const [flowState, changeFlowState] = useState('');
  const [streamingMessage, setStreamingMessage] =
    useState<StreamingState | null>(null);

  const [hasSSOTokens, setHasSSOTokens] = useState(() =>
    getSSOTokens(configId),
  );
  const [bypassToken, setBypassToken] = useState<string | null>(() =>
    getBypassToken(),
  );

  // Handle fullscreen toggle button click
  const handleToggleFullscreen = useCallback(() => {
    // Toggle the current active state
    const newFullscreenState = !isFullscreenActive;

    // Notify parent component about fullscreen change
    onFullscreenChange?.(newFullscreenState);

    // Notify connector (parent window) to resize iframe
    sendToggleFullscreen(newFullscreenState);

    // If minimizing and config had fullscreen default, change window state
    if (!newFullscreenState && fullscreen) {
      changeWindowState(defaultState === 'fullscreen' ? 'open' : defaultState);
    }
  }, [
    isFullscreenActive,
    fullscreen,
    changeWindowState,
    defaultState,
    onFullscreenChange,
  ]);

  // On iOS browsers, certain elements, especially those with position: fixed or z-index, are not rendered immediately,
  // especially if they are positioned off-screen or do not interact with the user.
  // This is a performance optimization in iOS browsers that prioritizes visible and interactive content.
  // By applying transform: translateZ(0) and temporarily hiding and showing the element (visibility: hidden to visibility: visible),
  // we force the GPU layer to render the element, ensuring that it is visible and interacts properly even without user interaction (scrolling or tapping).
  useEffect(() => {
    const widgetElement = document.getElementById('conversed-chat-widget-1848');
    if (widgetElement) {
      widgetElement.style.transform = 'translateZ(0)'; // triggers GPU layer
      widgetElement.style.visibility = 'hidden';
      requestAnimationFrame(() => {
        widgetElement.style.visibility = 'visible';
      });
    }
  }, []);

  useEffect(() => {
    // During handover (livechat), reopening the widget signals the user is active again - cancel the idle timer.
    // For LLM blocks the timer must persist across open/close cycles; only a new idleOptions event resets it.
    if (
      chatActivityStatus === 'online' &&
      flowState === 'handover' &&
      idleTimerRef.current
    ) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, [chatActivityStatus, flowState]);

  const handleSendMessageDisabledSetter = useCallback(
    (value: boolean) => {
      // If this feature is disabled, then don't block the user from sending messages.
      // This option is configurable in the Webclient config modal.
      if (!enableWaitingForBotResponseBlocker) {
        return;
      }

      // If the bot is not in the LLM state, then this blocker feature is not needed.
      if (flowState !== 'llm' && value) {
        return;
      }

      setIsSendMessageDisabled(value);
    },
    [enableWaitingForBotResponseBlocker, flowState],
  );

  // * Called by StreamingBubble once the typewriter flush is done, or immediately
  // * by the useEffect below when there is no active bubble to flush.
  const handleStreamComplete = useCallback(() => {
    const pending = pendingFinalMessageRef.current;
    if (!pending) {
      return;
    }
    pendingFinalMessageRef.current = null;
    // Clear typing before clearing streamingMessage so there is never a frame
    // where the indicator would briefly reappear between typewriter end and the
    // typing_off socket event arriving from Core.
    setTypingIndicator('typing_off');
    setStreamingMessage(null);
    // * Unconditional, matching streamStart's unconditional disable and the
    // * streamError/streamEnd cases - not gated by enableWaitingForBotResponseBlocker.
    setIsSendMessageDisabled(false);
    if (isSoundEnabled) {
      messageSound?.play().catch((playError) => {
        console.error('Playing sound is disabled by the browser', playError);
      });
    }
    addMessage(pending.direction, pending.params);

    // * Ack to Core that the streamed answer has finished animating, so it can
    // * advance to the next block without a following message overtaking it.
    ioService.send({
      event: 'streamRendered',
      data: { streamId: pending.streamId },
    });
  }, [isSoundEnabled, messageSound?.play, addMessage]);

  // * If the final message arrived when no streaming bubble was active
  // * (e.g. streamError already cleared it), complete immediately.
  useEffect(() => {
    if (streamingMessage === null && pendingFinalMessageRef.current !== null) {
      handleStreamComplete();
    }
  }, [streamingMessage, handleStreamComplete]);

  const requestData = useCallback((requestType: string) => {
    switch (requestType) {
      case 'location': {
        sendLocationRequest();
        break;
      }
      default: {
        break;
      }
    }
  }, []);

  const setUserID = useCallback(
    (data: IDData) => {
      try {
        const currentStorageItem = storage.get(configId);
        let dataToBeStored = { id: data.id };

        if (currentStorageItem) {
          const parsedData = JSON.parse(currentStorageItem);
          dataToBeStored = { ...parsedData, id: data.id };
        }
        storage.set(configId, dataToBeStored);
      } catch (_error) {
        storage.set(configId, { id: data.id });
      }
    },
    [configId],
  );

  // Handle chat window close action
  const handleCloseWindow = useCallback(
    (targetState: ChatWindowTypeState = 'closed') => {
      // Reset fullscreen state before closing
      if (isUserFullscreen !== null) {
        onFullscreenChange?.(false);
        sendToggleFullscreen(false);
      }

      // Close the window
      changeWindowState(targetState);
      sendWindowStatus({
        windowToggle: false,
        windowType: targetState,
      });
    },
    [changeWindowState, sendWindowStatus, isUserFullscreen, onFullscreenChange],
  );

  const clearSSOTokensAndSignal = useCallback(
    (errorMessage?: string) => {
      console.error('SSO verification failed - clearing tokens', errorMessage);
      patchStorage(configId, {
        sso: errorMessage ? { error: errorMessage } : null,
      });
      // * Trigger update to show MicrosoftSSOBlocker
      window.dispatchEvent(new Event('sso-tokens-updated'));
    },
    [configId],
  );

  const registerIOEvents = useCallback(() => {
    if (!registeredEvents.get()) {
      io.registerEvent({ event: 'text', callback: processMessage });
      io.registerEvent({ event: 'system', callback: processMessage });
      io.registerEvent({ event: 'image', callback: processMessage });
      io.registerEvent({
        event: 'imageWithButtons',
        callback: processMessage,
      });
      io.registerEvent({
        event: 'textWithButtons',
        callback: processMessage,
      });
      io.registerEvent({ event: 'carousel', callback: processMessage });
      io.registerEvent({ event: 'auth', callback: processMessage });
      io.registerEvent({ event: 'action', callback: processMessage });
      io.registerEvent({ event: 'user id', callback: processMessage });
      io.registerEvent({ event: 'user', callback: processMessage });
      io.registerEvent({ event: 'dataRequest', callback: processMessage });
      io.registerEvent({
        event: 'updateUserId',
        callback: processMessage,
      });
      io.registerEvent({ event: 'feedback', callback: processMessage });
      io.registerEvent({ event: 'dropdown', callback: processMessage });
      io.registerEvent({ event: 'end', callback: processMessage });
      io.registerEvent({ event: 'idleOptions', callback: processMessage });
      io.registerEvent({ event: 'flowState', callback: processMessage });
      io.registerEvent({ event: 'language', callback: processMessage });
      io.registerEvent({ event: 'SSOError', callback: processMessage });
      io.registerEvent({ event: 'streamStart', callback: processMessage });
      io.registerEvent({ event: 'textChunk', callback: processMessage });
      io.registerEvent({ event: 'streamStatus', callback: processMessage });
      io.registerEvent({ event: 'streamError', callback: processMessage });
      io.registerEvent({ event: 'streamEnd', callback: processMessage });
      registeredEvents.set();
    }
  }, [registeredEvents]);

  const processMessage = useCallback(
    (direction: MessageDirection, params: MessagePayload) => {
      switch (params.type) {
        case 'id': {
          setUserID(params.data);
          return;
        }
        case 'dataRequest': {
          requestData(params.data);
          return;
        }
        case 'action': {
          setTypingIndicator(params.data);
          return;
        }
        case 'updateUserId': {
          registeredEvents.delete();

          // If SSO is required, wait until SSO tokens are available before initializing WebSocket
          if (requireMicrosoftLogin && !bypassToken && !hasSSOTokens) {
            return;
          }

          setUserID({ id: params.data });
          io.reInitialize(
            configId,
            registerIOEvents,
            toggleConnectionStateBanner,
          );
          setMessages(params.data);
          return;
        }
        case 'end': {
          handleCloseWindow(defaultState);
          initState({
            windowType: defaultState,
            windowToggle: false,
          });
          resetUserAndReInitIOService();
          return;
        }
        case 'idleOptions': {
          // Clear any existing timer
          if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
          }

          if (typeof params?.data?.idleTimeout === 'undefined') {
            return;
          }

          // Start a new timer based on idleTimeout
          const idleTimeoutInMs = params.data.idleTimeout * 60 * 1000;
          idleTimerRef.current = setTimeout(() => {
            if (params.data.closeAfterIdleTimeout) {
              handleCloseWindow(defaultState);
            }

            sendWindowStatus({
              windowToggle: !params.data.closeAfterIdleTimeout,
              windowType: defaultState,
            });
            initState({
              windowType: defaultState,
              windowToggle: !params.data.closeAfterIdleTimeout,
            });

            resetUserAndReInitIOService();
          }, idleTimeoutInMs);

          return;
        }
        case 'flowState': {
          if (params.data.state !== 'llm') {
            // If the user is blocked from sending messages, then unblock the user when the bot is not in the LLM state
            handleSendMessageDisabledSetter(false);
          }

          changeFlowState(params.data.state);
          // Propagate the flow state up to App so Bubble can gate the 45-second
          // idle timeout to Handover blocks only.
          onFlowStateChange?.(params.data.state);
          return;
        }
        case 'language': {
          // Core reports the session language once the flow sets it, so the
          // widget's own texts follow the language the visitor picked.
          onLanguageChange?.(params.data.language);
          return;
        }
        case 'SSOError': {
          if (bypassToken !== null) {
            // * Bypass failure - clear the in-memory token, write user-friendly error to
            // * localStorage so MicrosoftSignIn reads it on mount, then show the blocker.
            storeBypassToken(null);
            setBypassToken(null);
            patchStorage(configId, {
              sso: {
                error:
                  params.data?.error ||
                  'We could not verify your session automatically. Please sign in with Microsoft.',
              },
            });
          } else {
            clearSSOTokensAndSignal(params.data?.error);
          }

          return;
        }
        case 'streamStart': {
          // * Explicitly disable the send button for the whole stream regardless
          // * of the enableWaitingForBotResponseBlocker / flowState guards.
          setIsSendMessageDisabled(true);
          setStreamingMessage({
            streamId: params.data.streamId,
            accumulatedText: '',
            phase: StreamPhase.Thinking,
            stage: null,
          });
          // * Start a watchdog: if no textChunk arrives within the window,
          // * clear the streaming state so the user is not stuck on "Thinking...".
          if (streamWatchdogRef.current) {
            clearTimeout(streamWatchdogRef.current);
          }
          streamWatchdogRef.current = setTimeout(() => {
            setStreamingMessage(null);
            setIsSendMessageDisabled(false);
          }, STREAM_WATCHDOG_MS);
          return;
        }
        case 'textChunk': {
          // * First chunk arrived - cancel the watchdog and flip to streaming immediately.
          if (streamWatchdogRef.current) {
            clearTimeout(streamWatchdogRef.current);
            streamWatchdogRef.current = null;
          }
          const chunkStreamId = params.data.streamId;
          const delta = params.data.delta;
          setStreamingMessage((previous) => {
            if (!previous || previous.streamId !== chunkStreamId) {
              return previous;
            }
            return {
              ...previous,
              accumulatedText: previous.accumulatedText + delta,
              phase: StreamPhase.Streaming,
            };
          });
          return;
        }
        case 'streamStatus': {
          // * Vector service confirmed - cancel the watchdog and update the status.
          if (streamWatchdogRef.current) {
            clearTimeout(streamWatchdogRef.current);
            streamWatchdogRef.current = null;
          }
          const statusStreamId = params.data.streamId;
          const newStage = params.data.stage;
          setStreamingMessage((previous) => {
            if (!previous || previous.streamId !== statusStreamId) {
              return previous;
            }
            // * Thinking -> Status on first stage event, or update stage within Status
            // * (searching -> generating). Never go backward from Streaming.
            if (
              previous.phase === StreamPhase.Thinking ||
              previous.phase === StreamPhase.Status
            ) {
              return {
                ...previous,
                phase: StreamPhase.Status,
                stage: newStage,
              };
            }
            return previous;
          });
          return;
        }
        case 'streamError': {
          // * Discard the partial streaming bubble and cancel the watchdog.
          if (streamWatchdogRef.current) {
            clearTimeout(streamWatchdogRef.current);
            streamWatchdogRef.current = null;
          }
          setStreamingMessage(null);
          // * Clear the typing indicator in case Core never sends typing_off
          // * (e.g. if the fallback callLLM also fails and goes to errorDestination).
          setTypingIndicator('typing_off');
          setIsSendMessageDisabled(false);
          return;
        }
        case 'streamEnd': {
          // * Clean, non-error close-out of a placeholder stream - discard the
          // * bubble and cancel the watchdog, same as streamError, but this
          // * isn't a failure so the typing indicator is left alone.
          if (streamWatchdogRef.current) {
            clearTimeout(streamWatchdogRef.current);
            streamWatchdogRef.current = null;
          }
          setStreamingMessage(null);
          setIsSendMessageDisabled(false);
          return;
        }
        default: {
          // * When the final authoritative streaming message arrives (tagged with
          // * streamId + final), let the typewriter finish before swapping.
          // * pendingFinalMessageRef stores the message; handleStreamComplete
          // * performs the actual addMessage once StreamingBubble calls onComplete.
          if (
            (params.type === 'text' || params.type === 'textWithButtons') &&
            params.options?.streamId &&
            params.options?.final
          ) {
            if (streamWatchdogRef.current) {
              clearTimeout(streamWatchdogRef.current);
              streamWatchdogRef.current = null;
            }
            pendingFinalMessageRef.current = {
              direction,
              params,
              streamId: params.options.streamId,
            };
            setStreamingMessage((previous) => {
              if (previous) {
                return { ...previous, completing: true };
              }
              // No active bubble: the useEffect above fires handleStreamComplete.
              return null;
            });
            return;
          }
          handleSendMessageDisabledSetter(false);
          if (isSoundEnabled) {
            messageSound?.play().catch((playError) => {
              // This happens because browsers may block autoplay of audio due to user interaction requirements or other restrictions.
              console.error(
                'Playing sound is disabled by the browser',
                playError,
              );
            });
          }

          addMessage(direction, params);
        }
      }
    },
    [
      setUserID,
      requestData,
      registerIOEvents,
      setMessages,
      addMessage,
      isSoundEnabled,
      messageSound?.play,
      registeredEvents?.delete,
      handleCloseWindow,
      initState,
      defaultState,
      handleSendMessageDisabledSetter,
      clearSSOTokensAndSignal,
      configId,
      requireMicrosoftLogin,
      hasSSOTokens,
      bypassToken,
      onFlowStateChange,
      onLanguageChange,
    ],
  );

  const resetUserAndReInitIOService = useCallback(() => {
    // If SSO is required, wait until SSO tokens are available before initializing WebSocket
    if (requireMicrosoftLogin && !bypassToken && !hasSSOTokens) {
      return;
    }

    localStorageService.delete(configId);
    // * The new session reports its own flow state. A stale 'llm' state would
    // * show the text input before the new user id arrives.
    changeFlowState('');
    registeredEvents.delete();
    ioService.reInitialize(
      configId,
      registerIOEvents,
      toggleConnectionStateBanner,
    );
    handleSendMessageDisabledSetter(false);
  }, [
    registerIOEvents,
    registeredEvents,
    handleSendMessageDisabledSetter,
    configId,
    requireMicrosoftLogin,
    bypassToken,
    hasSSOTokens,
    toggleConnectionStateBanner,
  ]);

  const registerIOEventsOnChatEnabled = useCallback(async () => {
    // If SSO is required, wait until SSO tokens are available before initializing WebSocket
    if (requireMicrosoftLogin && !bypassToken && !hasSSOTokens) {
      // * Re-enable the chat UI without connecting so the render falls through to the
      // * MS login screen - otherwise a kicked tab with no tokens stays stuck on the Blocker.
      onEnableChat();

      return;
    }

    // After reInitialize tears down the old socket, the server-side disconnect handler
    // immediately broadcasts activityState: 'offline'. We pass onConnected to re-send
    // 'online' once the new socket is established, so the live agent dashboard reflects
    // the correct status after the user clicks "Continue here".
    io.reInitialize(
      configId,
      registerIOEvents,
      toggleConnectionStateBanner,
      () => {
        setSendChatStatus({ status: 'online' }, configId);
      },
    );
    handleSendMessageDisabledSetter(false);
    onEnableChat();
  }, [
    onEnableChat,
    registerIOEvents,
    handleSendMessageDisabledSetter,
    configId,
    requireMicrosoftLogin,
    bypassToken,
    hasSSOTokens,
    toggleConnectionStateBanner,
    setSendChatStatus,
  ]);

  const handleAttachmentCancel = useCallback(
    (index: number) => {
      setAttachments(attachments.splice(index, 1));
    },
    [attachments],
  );

  const getIOSubmitPayload = useCallback(
    (value, postbackType?: string): MessageData => {
      const userId = getUserId(configId);
      if (typeof value === 'string') {
        handleSendMessageDisabledSetter(true);
        return {
          id: userId,
          type: 'message',
          message: value,
        };
      }

      const response: MessageData = {
        id: userId,
        type: 'postback',
        message: value,
      };

      if (postbackType) {
        Object.assign(response, {
          postbackType: postbackType,
        });
      }

      return response;
    },
    [handleSendMessageDisabledSetter, configId],
  );

  const submitHandler = useCallback(
    ({ value, attachments, shouldHideMessage, postbackType }) => {
      if (isSendMessageDisabled) {
        throw new Error('Waiting for the bot response, please wait');
      }

      if (value && value !== '') {
        const payload: MessagePayload = {
          type: 'text',
          data: {
            text: typeof value === 'string' ? value : value.title,
          },
        };

        const close =
          value?.options?.webclient?.close || value?.options?.close || false;

        // * If webclient options is set for button carry that over to the next message
        if (typeof value?.options !== 'undefined') {
          Object.assign(payload, {
            options: {
              webclient: {
                ...payload?.options?.webclient,
                ...(value.options?.webclient || value.options),
              },
            },
          });
        }

        if (close) {
          handleCloseWindow('closed');
        }

        io.send({
          event: 'message',
          data: getIOSubmitPayload(value, postbackType),
        });

        if (!shouldHideMessage) {
          addMessage('in', payload);
        }
      }

      if (Array.isArray(attachments) && attachments.length > 0) {
        attachments.forEach((attachment) => {
          io.send({
            event: 'image',
            data: {
              id: getUserId(configId),
              type: 'image',
              data: attachment,
            },
          });

          const imagePayload: MessagePayload = {
            type: 'image',
            data: {
              url: attachment.base64,
            },
          };

          addMessage('in', imagePayload);
        });
      }
    },
    [
      handleCloseWindow,
      addMessage,
      getIOSubmitPayload,
      isSendMessageDisabled,
      configId,
    ],
  );

  const getWebSocketOptions = useCallback(() => {
    const options = {
      ...querystringService.getAsRefOptions(),
      analytics: analyticsService.createAnalyticData(),
      data,
      metadata: {},
    };

    if (refParam) {
      Object.assign(options, {
        ref: {
          ref: refParam,
          source: 'conversed-webclient',
        },
      });
    }

    if (revisionId) {
      Object.assign(options, {
        metadata: {
          ...options?.metadata,
          revision: revisionId,
        },
      });
    }

    if (configId) {
      Object.assign(options, {
        metadata: {
          ...options?.metadata,
          configId,
        },
      });
    }

    if (!requireMicrosoftLogin) {
      return options;
    }

    // * SSO bypass path: forward the raw JWT to core for server-side decoding and verification
    if (bypassToken) {
      Object.assign(options, { sso: { bypassToken } });

      return options;
    }

    // * Standard MS SSO path: include all tokens
    const { sso } = getStorage(configId);

    if (sso?.accessToken && sso?.refreshToken && sso?.idToken && sso?.upn) {
      Object.assign(options, {
        sso: {
          accessToken: sso.accessToken,
          refreshToken: sso.refreshToken,
          idToken: sso.idToken,
          upn: sso.upn,
          cao: sso.cao || null,
        },
      });
    }

    return options;
  }, [
    requireMicrosoftLogin,
    bypassToken,
    refParam,
    revisionId,
    data,
    configId,
  ]);

  // * `initWebSocket` carries no Voice mode, so this effect connects the socket
  // * for a VoiceOnly widget too. See the TODO on `showChatBody` below.
  useEffect(() => {
    if (!initWebSocket) {
      return;
    }

    // If SSO is required, wait until SSO tokens are available before initializing WebSocket
    if (requireMicrosoftLogin && !bypassToken && !hasSSOTokens) {
      return;
    }

    // * Don't auto-connect when the chat is disabled - the Blocker handles explicit reconnect.
    // * This prevents a kicked tab from racing to connect when tokens arrive from another tab.
    if (!chatEnabled) {
      return;
    }

    const URL = Api.CONVERSED_SERVER;
    // Initialize the socket connection
    ioService.init(
      URL,
      botId,
      configId,
      registerIOEvents,
      toggleConnectionStateBanner,
    );

    return () => {
      // * Remove listeners
      registeredEvents.delete();
    };
  }, [
    initWebSocket,
    registerIOEvents,
    toggleConnectionStateBanner,
    registeredEvents.delete,
    botId,
    configId,
    requireMicrosoftLogin,
    bypassToken,
    hasSSOTokens,
    chatEnabled,
  ]);

  useEffect(() => {
    if (!initWebSocket) {
      return;
    }

    if (requireMicrosoftLogin && !bypassToken && !hasSSOTokens) {
      return;
    }

    // * Don't auto-connect when the chat is disabled - the Blocker handles explicit reconnect.
    if (!chatEnabled) {
      return;
    }

    const initializeChat = async () => {
      // * MS SSO token refresh - only relevant for the popup SSO path (bypass never sets hasSSOTokens)
      if (requireMicrosoftLogin && hasSSOTokens) {
        const { sso: storedSso } = getStorage(configId);
        const storedIdToken = storedSso?.idToken ?? null;
        const storedRefreshToken = storedSso?.refreshToken ?? null;

        // * Also refresh when id_token is absent but refresh_token is present - a partial
        // * localStorage state that would otherwise cause core to reject the connection.
        if (
          storedRefreshToken &&
          (!storedIdToken || isJwtExpired(storedIdToken))
        ) {
          const newTokens = await refreshSSOTokens(storedRefreshToken);

          if (!newTokens) {
            clearSSOTokensAndSignal('Session expired. Please sign in again.');
            return;
          }

          // * Do NOT dispatch 'sso-tokens-updated' here - hasSSOTokens is already true
          // * and dispatching it would re-run this effect, causing an infinite refresh loop.
          const currentSso = getStorage(configId).sso;
          patchStorage(configId, {
            sso: { ...currentSso, ...newTokens },
          });
        }
      }

      const options = getWebSocketOptions();
      const id = getUserId(configId);
      // Check if this connection is already established via the 'connected' flag in ioService.
      // Only send initialization messages if there is no connection or we're deliberately starting a new chat.
      const isNewSession = !io?.isConnected() || !id;

      // * If the user has an id, then restart the previous user chat session, otherwise start a new chat session
      if (id && isNewSession) {
        io.send({
          event: 'restartFlow',
          data: { id, type: 'restart', options },
        });

        handleSendMessageDisabledSetter(false);
      } else if (!id) {
        io.send({
          event: 'new user',
          data: { id, type: 'intro', options },
        });
      }
    };

    initializeChat();
  }, [initWebSocket, requireMicrosoftLogin, bypassToken, hasSSOTokens]);

  // When the session is reset in another tab (e.g., idle timeout fired there and
  // localStorageService.delete(configId) was called), mirror the expiry in this tab
  // so that duplicated web clients do not outlive the configured idle timeout.
  // The storage event fires in every tab except the one that made the change, which
  // is exactly what we want - the originating tab handles its own reset directly.
  useEffect(() => {
    const handleSessionReset = (storageEvent: StorageEvent) => {
      // localStorageService.delete(configId) calls localStorage.removeItem,
      // which produces a storage event with newValue === null.
      // Ignore unrelated key changes or key writes (newValue !== null).
      if (storageEvent.key !== configId || storageEvent.newValue !== null) {
        return;
      }

      // Cancel any locally running idle timer to avoid a redundant second reset.
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }

      // Mirror the session expiry: close the chat window and start a fresh session.
      handleCloseWindow(defaultState);
      sendWindowStatus({
        windowToggle: false,
        windowType: defaultState,
      });
      initState({
        windowType: defaultState,
        windowToggle: false,
      });
      resetUserAndReInitIOService();
    };

    window.addEventListener('storage', handleSessionReset);
    return () => {
      window.removeEventListener('storage', handleSessionReset);
    };
  }, [
    configId,
    defaultState,
    handleCloseWindow,
    sendWindowStatus,
    initState,
    resetUserAndReInitIOService,
  ]);

  // Listen for changes to localStorage (SSO token updates)
  useEffect(() => {
    // Check SSO tokens on mount
    setHasSSOTokens(getSSOTokens(configId));

    // Listen for storage events (triggered when localStorage changes in other tabs/windows)
    const handleSSOStorageChange = (storageEvent: StorageEvent) => {
      if (
        storageEvent.key === configId ||
        storageEvent.key === null // null means clear() was called
      ) {
        setHasSSOTokens(getSSOTokens(configId));
      }
    };

    // Listen for custom event (triggered when SSO login completes in same window)
    const handleSSOUpdate = () => {
      setHasSSOTokens(getSSOTokens(configId));
    };

    window.addEventListener('storage', handleSSOStorageChange);
    window.addEventListener('sso-tokens-updated', handleSSOUpdate);

    return () => {
      window.removeEventListener('storage', handleSSOStorageChange);
      window.removeEventListener('sso-tokens-updated', handleSSOUpdate);
    };
  }, [configId]);

  // * "Updating conversation" supersedes the MS login screen - a kicked tab should
  // * always prompt the user to explicitly take over rather than auto-reconnect.
  if (!chatEnabled) {
    return (
      <div
        className={elementClass}
        style={{
          borderColor: headerColor,
        }}>
        {isFullscreenActive && (
          <div className='chat-window-blocker-fullscreen-exit'>
            <FullscreenToggleButton
              isFullscreen={isFullscreenActive}
              onToggle={handleToggleFullscreen}
            />
          </div>
        )}
        <Blocker onEnableChat={registerIOEventsOnChatEnabled} />
      </div>
    );
  }

  if (requireMicrosoftLogin && !bypassToken && !hasSSOTokens) {
    return (
      <div
        className={elementClass}
        style={{
          borderColor: headerColor,
        }}>
        <MicrosoftSSOBlocker />
      </div>
    );
  }
  // * Never allow text input if banner message is set to error and exists
  const allowTextInputActual =
    bannerMessage?.length && bannerStatus === 'error' ? false : allowTextInput;

  const isVoiceEnabled =
    voiceMode !== VoiceMode.Chat && Boolean(voice?.elevenLabsAgentId);

  // * TODO: a Voice A.I. Studio hides the chat body, but does not stop the chat.
  // * The socket still connects and core still runs the intro flow, so the visitor
  // * gets a ChatBotUserSession and the flow's side effects fire unseen (LLM
  // * tokens, databaseUpdate writes, webhooks). The call itself is unaffected -
  // * the browser talks to ElevenLabs directly.
  // * Not gated here on purpose: the gate belongs in App.tsx `shouldInitWebSocket`
  // * and the socket effects above, the shared entry point for every widget. A
  // * condition there changes the session lifecycle for chat-only deployments too,
  // * so it needs a source gate plus a Chat/ChatAndVoice regression pass - a
  // * socket-lifecycle refactor, not a one-liner.
  const showChatBody =
    voiceMode !== VoiceMode.VoiceOnly && activeTab === 'chat';
  const showVoiceUnavailable =
    voiceMode === VoiceMode.VoiceOnly && !isVoiceEnabled;
  const showVoiceTabBar =
    voiceMode === VoiceMode.ChatAndVoice && isVoiceEnabled;

  return (
    <div className={elementClass}>
      <ChatTitle
        onClose={onClose}
        bannerMessage={bannerMessage}
        bannerStatus={bannerStatus}
        isFullscreen={isFullscreenActive}
        onToggleFullscreen={handleToggleFullscreen}
      />
      {showChatBody && (
        <>
          <Messages
            messages={messages}
            submitHandler={submitHandler}
            typingState={typingState}
            streamingMessage={streamingMessage}
            onStreamComplete={handleStreamComplete}
          />
          <Attachments
            show={attachments.length > 0}
            attachments={attachments}
            handleAttachmentCancelClick={handleAttachmentCancel}
          />
          <MessageBox
            allowTextInput={allowTextInputActual}
            setAttachments={setAttachments}
            attachments={attachments}
            submitHandler={submitHandler}
            messages={messages}
            flowState={flowState}
            setSendChatStatus={setSendChatStatus}
            changeChatActivityStatus={changeChatActivityStatus}
          />
        </>
      )}
      {showVoiceUnavailable && (
        <div className='voice-unavailable'>{voiceMessages.unavailable}</div>
      )}
      {/* Only rendered when Voice tab is active - the widget appends its floating
          overlay to document.body so display:none on a parent has no effect. */}
      {activeTab === 'voice' && isVoiceEnabled && voice?.elevenLabsAgentId && (
        <VoicePanel
          agentId={voice.elevenLabsAgentId}
          onCallStatusChange={setIsVoiceCallActive}
        />
      )}
      {showVoiceTabBar && (
        <>
          {pendingTab !== null && (
            <div className='voice-tab-switch-confirm'>
              <span className='voice-tab-switch-confirm-text'>
                {voiceMessages.tabSwitchConfirm.question}
              </span>
              <div className='voice-tab-switch-confirm-actions'>
                <button
                  type='button'
                  className='voice-tab-switch-confirm-button voice-tab-switch-confirm-button--cancel'
                  onClick={handleCancelTabSwitch}>
                  {voiceMessages.tabSwitchConfirm.cancel}
                </button>
                <button
                  type='button'
                  className='voice-tab-switch-confirm-button voice-tab-switch-confirm-button--confirm'
                  style={{ backgroundColor: headerColor }}
                  onClick={handleConfirmTabSwitch}>
                  {voiceMessages.tabSwitchConfirm.confirm}
                </button>
              </div>
            </div>
          )}
          <ChatVoiceTabBar
            activeTab={activeTab}
            onTabChange={handleTabChange}
            activeColor={headerColor}
            messages={voiceMessages}
            newBadge={resolveVoiceNewBadge(voice, voiceMessages)}
          />
        </>
      )}
    </div>
  );
};

export default ChatWindow;
